import React, {
  createContext,
  PropsWithChildren,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useServices } from "../hooks/useServices";
import { useAuth0 } from "@auth0/auth0-react";
import { useCachedAccessToken } from "@/hooks/useCachedAccessToken";
import usePiiFeatureEnabled from "@/hooks/usePiiFeatureEnabled";
import Conversation, {
  emptyConversation,
} from "../models/conversation/Conversation";
import Message, {
  ConversationLanguage,
  MessageRating,
  TEMP_AI_MESSAGE,
  TEMP_AI_MESSAGE_ID,
  TEMP_HUMAN_MESSAGE,
  TEMP_HUMAN_MESSAGE_ID,
} from "../models/conversation/Message";
import { SearchOption } from "../models/base/SearchOption";
import { useDocumentReferenceStore } from "../stores/document-reference.store";
import { SendMessageContextFilter } from "../services/messageService";
import type { ConversationWorkspaceDocumentSnapshot } from "@/lib/buildConversationWorkspaceDocuments";
import { type OverridesSummary } from "@/lib/pii";
import { uuidv4 } from "@capacit-com/lara-law-shared-ui/uuid";
import ToneOfVoice from "@/models/tone-of-voice/toneOfVoice";
import {
  showErrorToastWithAction,
  showWarningToast,
} from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import { restError } from "@/models/client/RestResponse";
import {
  convertContextFiltersToWorkspaceContext,
  convertSearchOptionsToSource,
} from "@/pages/chat/utils/PineconeQueries";
import { useNewConversationFiltersStore } from "@/stores/new-conversation-filters.store";
import camelcaseKeys from "camelcase-keys";
import { useLocation } from "react-router-dom";
import {
  ackChatGeneration,
  ChatDoneEvent,
  ChatStreamCallbacks,
  listPendingGenerations,
  startChatGeneration,
  subscribeChatGeneration,
} from "@/services/chatStreamService";
import {
  GENERATION_FAILED_STATUS,
  getGenerationErrorText,
  isExpiredGenerationError,
  isStreamAbortError,
  isRetryableTransportStreamError,
  resolvePersistableStreamError,
} from "@/lib/chatGenerationErrors";
import {
  clearPersistedInFlightStream,
  getPersistedInFlightStream,
  persistInFlightStream,
  persistInFlightStreamMap,
  synthesizeLatestMessageFromStreamState,
} from "@/lib/persistedInFlightStream";
import {
  getPendingStream,
  listPendingStreams,
  removePendingStream,
  upsertPendingStream,
  type PendingStreamRecovery,
  type PendingStreamRecoveryPhase,
} from "@/lib/pendingStreamRecovery";
import { mergePendingRecoveries } from "@/lib/streamRecoveryMerge";
import {
  isDoneEventAlreadyPersisted,
  isFailedStreamAlreadyPersisted,
  isSuccessfulStreamAlreadyPersisted,
  releasePersistGenerationLock,
  releaseReconcileGenerationLock,
  runExclusiveReconcile,
  tryAcquirePersistGenerationLock,
  tryAcquireReconcileGenerationLock,
} from "@/lib/streamRecoveryIdempotency";
import {
  clearStreamPersistFailures,
  handleStreamPersistFailure,
} from "@/lib/streamPersistAttempts";
import { parseTenantIdFromAccessToken } from "@/utils/auth";
import useUserStore from "@/stores/user.store";
import {
  createInFlightStreamState,
  createEmptyStreamAccumulator,
  hasActiveNonTerminalStream,
  type InFlightStreamState,
} from "@/lib/inFlightStreamState";
import {
  trimMemoryForRetry,
  willRetryResetConversation,
} from "@/lib/chatRetry";
import { ConfirmationDialog } from "@capacit-com/lara-law-shared-ui";
import { RefreshCw } from "lucide-react";
import { processReferences } from "@/utils/referenceProcessing";
import { saveChatScrollStateFromDom } from "@/lib/chatScrollMemory";
import {
  createTitleWriteState,
  saveConversationTitle,
} from "@/lib/conversationTitleWrites";

export type ChatConnectionState = "Connected" | "Disconnected";

type SendMessageOptions = {
  retryFailedAiMessageId?: string;
  /** PII-free override summary (LAR-27) recorded for admin audit after send. */
  piiOverride?: OverridesSummary;
};

export type ConversationHubContextProps = {
  conversations: Conversation[];
  selectedConversation: Conversation | undefined;
  latestMessage: Message | null;
  waitingForResponse: boolean;
  loading: boolean;
  isLoadingMessages: { isLoading: boolean; conversationId: string | null };
  connectionState: ChatConnectionState;
  selectedLanguage: ConversationLanguage | null;
  setSelectedLanguage: (language: ConversationLanguage | null) => void;
  useChainOfThought: boolean;
  setUseChainOfThought: (useChainOfThought: boolean) => void;
  toneOfVoice: ToneOfVoice | undefined;
  setToneOfVoice: (toneOfVoice: ToneOfVoice | undefined) => void;

  selectConversation: (conversation: Conversation | undefined) => Promise<void>;
  selectConversationById: (conversationId: string) => Promise<void>;
  deleteConversation: (conversation: Conversation) => Promise<void>;
  rateMessage: (message: Message, rating: MessageRating) => Promise<void>;
  manualReconnect: () => Promise<void>;
  sendMessage: (
    text: string,
    searchOptions: SearchOption[],
    filter: SendMessageContextFilter[],
    workspaceDocuments?: ConversationWorkspaceDocumentSnapshot[],
    options?: SendMessageOptions
  ) => Promise<void>;
  retryFailedResponse: (failedAiMessage: Message) => Promise<void>;
  pageNumber: number;
  pageCount: number;
  handleNextPage: () => Promise<void>;
  handlePreviousPage: () => Promise<void>;
  setPageNumber: (page: number) => Promise<void>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const ConversationContext =
  createContext<ConversationHubContextProps | null>(null);

const ConversationHubProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { setSelectedSource, setWorkspaceContext, resetFilters, setFilters } =
    useNewConversationFiltersStore();
  const { setDocumentReferences, reset } = useDocumentReferenceStore();
  const conversationService = useServices().items.conversation;
  const messageService = useServices().items.message;
  const piiOverrideService = useServices().items.piiOverride;
  const piiFeatureEnabled = usePiiFeatureEnabled();
  const { isAuthenticated } = useAuth0();
  const getCachedToken = useCachedAccessToken();
  const me = useUserStore((state) => state.me);
  const recoveryUserId = me?.id ?? null;
  const recoveryUserIdRef = useRef<string | null>(recoveryUserId);
  recoveryUserIdRef.current = recoveryUserId;
  const location = useLocation();

  /**
   * Checks if the user is currently viewing a reference page
   * Reference pages should not have their document reference store updated during streaming
   */
  const isOnReferencePage = (): boolean => {
    const path = location.pathname;
    // Check if the path matches any of the reference view patterns
    return (
      path.includes("/chat/legislation/") || // Legislation PDF/HTML views
      path.includes("/chat/workspace/") // Workspace document views
    );
  };

  const [selectedConversation, setSelectedConversation] = useState<
    Conversation | undefined
  >(undefined);
  const selectedConversationRef = useRef<Conversation | undefined>(undefined);
  const [latestMessage, setLatestMessage] = useState<Message | null>(null);
  const latestMessageRef = useRef<Message | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [waitingForResponse, setWaitingForResponse] = useState<boolean>(false);
  const [retryResetConfirmation, setRetryResetConfirmation] =
    useState<Message | null>(null);

  // On first render, generate a unique session ID for the user
  const [sessionId] = useState<string>(uuidv4());

  const [selectedLanguage, setSelectedLanguage] =
    useState<ConversationLanguage | null>({
      name: "Danish",
      code: "da",
    } as ConversationLanguage);

  const [useChainOfThought, setUseChainOfThought] = useState<boolean>(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState<{
    isLoading: boolean;
    conversationId: string | null;
  }>({ isLoading: false, conversationId: null });
  const [loading, setLoading] = useState<boolean>(true);
  const [toneOfVoice, setToneOfVoice] = useState<ToneOfVoice | undefined>(
    undefined
  );

  const [connectionState, setConnectionState] = useState<ChatConnectionState>(
    navigator.onLine ? "Connected" : "Disconnected"
  );

  // Add these new state variables
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageCount, setPageCount] = useState<number>(1);
  const pageSize = 30; // You can adjust this value

  // Buffer for characters that have arrived from SignalR
  const typingBufferRef = useRef<string>("");
  // Total chars we’ve already queued (typed or waiting in the buffer)
  const queuedCharCountRef = useRef<number>(0);
  // Timeout handle (adaptive delay)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Holder for the final “closed” payload while the buffer is still flushing
  const pendingClosedMessageRef = useRef<Message | null>(null);
  // Separate buffer & counter for chain‑of‑thought streaming
  const cotBufferRef = useRef<string>("");
  const queuedCotCountRef = useRef<number>(0);
  // Separate buffer & counter for revised prompt streaming
  const revisedPromptBufferRef = useRef<string>("");
  const queuedRevisedPromptCountRef = useRef<number>(0);
  // Separate buffer & counter for StatusLong streaming
  const statusLongBufferRef = useRef<string>("");
  const queuedStatusLongCountRef = useRef<number>(0);
  const referenceReplacementsRef = useRef<Record<string, string>>({});
  const streamTextRef = useRef<string>("");
  const streamControllerRef = useRef<AbortController | null>(null);
  const selectConversationRunIdRef = useRef(0);
  const streamStateRef = useRef<{
    text: string;
    chainOfThoughtMessage: string;
    revisedPrompt: string;
    statusShort: string;
    statusLong: string;
    references: Message["references"];
    lemmatizedText: Record<string, string>;
  }>({
    text: "",
    chainOfThoughtMessage: "",
    revisedPrompt: "",
    statusShort: "",
    statusLong: "",
    references: [],
    lemmatizedText: {},
  });
  const inFlightHumanMessageIdRef = useRef<string | null>(null);
  const inFlightHumanMessagePersistedRef = useRef(false);
  const streamHandoffDoneRef = useRef(false);
  const inFlightStreamsRef = useRef<Map<string, InFlightStreamState>>(
    new Map()
  );
  const activeStreamConversationIdRef = useRef<string | null>(null);
  const generationIdRef = useRef<string | null>(null);
  const streamTenantIdRef = useRef<string | null>(null);
  const lastChunkSeqRef = useRef(0);
  // Which titles are already saved per conversation, and the write in flight. The ordering and
  // de-duplication rules live in @/lib/conversationTitleWrites (LAR-449).
  const titleWritesRef = useRef(createTitleWriteState());
  const streamContextRef = useRef<{
    conversationId: string;
    languageCode: string;
    humanText: string;
    searchOptions: SearchOption[];
    filter: SendMessageContextFilter[];
    workspaceDocuments?: ConversationWorkspaceDocumentSnapshot[];
    documentWorkspaceMap: Record<string, string>;
    isRetry: boolean;
  } | null>(null);
  const STREAM_PERSIST_THROTTLE_MS = 400;
  const streamPersistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const streamPersistConversationIdRef = useRef<string | null>(null);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  /* __________________Connection Setup__________________ */
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    void fetchConversations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Handle reconnection when the user goes offline and comes back online
  useEffect(() => {
    const handleOffline = () => {
      setConnectionState("Disconnected");
      setLoading(true);
    };

    const handleOnline = () => {
      void manualReconnect();
    };

    // Add event listeners for offline and online
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      // Clean up event listeners
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Streams one or more characters at a time with an adaptive delay
   *  (large backlogs → larger burst and shorter delay down to 1 ms).
   *  Order: CoT → revisedPrompt → statusLong → answer text
   */
  const startTypingInterval = (baseMessage: Message) => {
    if (typingTimeoutRef.current) return; // already running

    const BASE_DELAY = 50; // slowest typing speed (ms)
    const FASTEST = 1; // cap so the UI can paint

    /** Drain up to `burst` characters across the four buffers
     *    (priority: CoT → revisedPrompt → statusLong → text)
     *    and update the message once.
     */
    const drainBurst = (burst: number) => {
      let consumed = 0;

      const take = (buf: React.MutableRefObject<string>) => {
        if (buf.current.length === 0 || consumed >= burst) return "";
        const slice = buf.current.slice(0, burst - consumed);
        buf.current = buf.current.slice(slice.length);
        consumed += slice.length;
        return slice;
      };

      // Pull slices in priority order
      const cot = take(cotBufferRef);
      const rp = take(revisedPromptBufferRef);
      const sl = take(statusLongBufferRef);
      const txt = take(typingBufferRef);

      if (consumed === 0) return; // nothing drained

      // Single React state update per burst
      setLatestMessage((prev) => {
        const base =
          prev ??
          ({
            ...baseMessage,
            text: "",
            chainOfThoughtMessage: "",
            revisedPrompt: "",
            statusLong: "",
          } as Message);

        latestMessageRef.current = {
          ...base,
          chainOfThoughtMessage: (base.chainOfThoughtMessage ?? "") + cot,
          revisedPrompt: (base.revisedPrompt ?? "") + rp,
          statusLong: (base.statusLong ?? "") + sl,
          text: (base.text ?? "") + txt,
        };
        return latestMessageRef.current;
      });
    };

    const schedule = () => {
      // Calculate total backlog
      const backlog =
        cotBufferRef.current.length +
        revisedPromptBufferRef.current.length +
        statusLongBufferRef.current.length +
        typingBufferRef.current.length;

      if (backlog === 0) {
        // Flush complete → handle close payload & cleanup
        if (pendingClosedMessageRef.current) {
          const closedPayload = pendingClosedMessageRef.current;
          pendingClosedMessageRef.current = null;

          const mergedMessages = (messages: Message[]) => [
            ...messages.filter(
              (m) => m.id !== TEMP_AI_MESSAGE_ID && m.id !== closedPayload.id
            ),
            closedPayload,
          ];

          setSelectedConversation((prev) =>
            prev && prev.id === closedPayload.conversationId
              ? {
                  ...prev,
                  messages: mergedMessages(prev.messages ?? []),
                }
              : prev
          );

          setConversations((prevConversations) =>
            prevConversations.map((conversation) =>
              conversation.id === closedPayload.conversationId
                ? {
                    ...conversation,
                    messages: mergedMessages(conversation.messages ?? []),
                  }
                : conversation
            )
          );

          markStreamTerminal(closedPayload.conversationId);
          setLatestMessage(null);
          latestMessageRef.current = null;
          queuedCharCountRef.current = 0;
          queuedCotCountRef.current = 0;
          queuedRevisedPromptCountRef.current = 0;
          queuedStatusLongCountRef.current = 0;
          resetStreamState();
          streamControllerRef.current = null;
          setWaitingForResponse(false);
        }

        typingTimeoutRef.current = null;
        return; // nothing more to stream
      }

      // Drain a burst whose size adapts to backlog
      const burst = Math.min(25, 1 + Math.floor(backlog / 40)); // tweak 40 to taste
      drainBurst(burst);

      // halves the delay about every 100 queued chars,
      // but never goes below FASTEST.
      const ratio = backlog / 100; // tweak "100" to taste
      const delay = pendingClosedMessageRef.current?.isClosed
        ? FASTEST
        : Math.max(FASTEST, BASE_DELAY / (1 + ratio));

      typingTimeoutRef.current = setTimeout(schedule, delay);
    };

    // Kick off the first tick immediately
    schedule();
  };

  /* __________________Connection Handlers_________________ */
  const manualReconnect = async () => {
    setConnectionState(navigator.onLine ? "Connected" : "Disconnected");
    if (navigator.onLine) {
      setLoading(false);
      await fetchConversations(pageNumber);
    }
  };

  /* __________________Response Handlers__________________ */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onUpdateTitleConversation = (rawPayload: any) => {
    const payload: {
      id: string;
      title: string;
    } = camelcaseKeys(rawPayload, { deep: true });
    // Update the selected conversation's title
    setSelectedConversation((prevSelected) => {
      if (prevSelected && prevSelected.id === payload.id) {
        return {
          ...prevSelected,
          title: payload.title,
        };
      }
      return prevSelected;
    });

    // Update the title in the conversations array
    setConversations((prevConversations) =>
      prevConversations.map((conv) =>
        conv.id === payload.id ? { ...conv, title: payload.title } : conv
      )
    );
  };

  const resetStreamState = () => {
    referenceReplacementsRef.current = {};
    streamTextRef.current = "";
    streamStateRef.current = createEmptyStreamAccumulator();
  };

  const upsertPendingLedger = (partial: {
    generationId: string;
    conversationId: string;
    userId: string;
    tenantId: string;
    lastChunkSeq?: number;
    phase?: PendingStreamRecoveryPhase;
    streamContext?: InFlightStreamState["streamContext"];
    doneEvent?: ChatDoneEvent | null;
    errorMessage?: string | null;
    inFlightHumanMessageId?: string | null;
    inFlightHumanMessagePersisted?: boolean;
  }) => {
    if (!partial.userId) {
      return;
    }

    const existing = getPendingStream(partial.userId, partial.generationId);
    upsertPendingStream(partial.userId, {
      generationId: partial.generationId,
      conversationId: partial.conversationId,
      userId: partial.userId,
      tenantId: partial.tenantId,
      lastChunkSeq: partial.lastChunkSeq ?? existing?.lastChunkSeq ?? 0,
      phase: partial.phase ?? existing?.phase ?? "streaming",
      streamContext: partial.streamContext ?? existing?.streamContext ?? null,
      doneEvent: partial.doneEvent ?? existing?.doneEvent ?? null,
      errorMessage: partial.errorMessage ?? existing?.errorMessage ?? null,
      inFlightHumanMessageId:
        partial.inFlightHumanMessageId ??
        existing?.inFlightHumanMessageId ??
        null,
      inFlightHumanMessagePersisted:
        partial.inFlightHumanMessagePersisted ??
        existing?.inFlightHumanMessagePersisted ??
        false,
      persistedAt: Date.now(),
    });
  };

  const clearRecoveryState = (
    userId: string | null | undefined,
    conversationId: string,
    generationId: string | null | undefined
  ) => {
    if (generationId && userId) {
      removePendingStream(userId, generationId);
      clearStreamPersistFailures(userId, generationId);
    }
    if (userId) {
      clearPersistedInFlightStream(userId, conversationId);
    }
  };

  const buildActiveStreamState = (
    conversationId: string
  ): InFlightStreamState => {
    const existing =
      inFlightStreamsRef.current.get(conversationId) ??
      createInFlightStreamState(conversationId, generationIdRef.current ?? "");

    return {
      ...existing,
      conversationId,
      generationId: generationIdRef.current ?? existing.generationId,
      lastChunkSeq: lastChunkSeqRef.current,
      streamHandoffDone: streamHandoffDoneRef.current,
      waitingForResponse: true,
      latestMessage: latestMessageRef.current,
      streamState: { ...streamStateRef.current },
      streamText: streamTextRef.current,
      referenceReplacements: { ...referenceReplacementsRef.current },
      typingBuffer: typingBufferRef.current,
      cotBuffer: cotBufferRef.current,
      revisedPromptBuffer: revisedPromptBufferRef.current,
      statusLongBuffer: statusLongBufferRef.current,
      queuedCharCount: queuedCharCountRef.current,
      queuedCotCount: queuedCotCountRef.current,
      queuedRevisedPromptCount: queuedRevisedPromptCountRef.current,
      queuedStatusLongCount: queuedStatusLongCountRef.current,
      pendingClosedMessage: pendingClosedMessageRef.current,
      inFlightHumanMessageId: inFlightHumanMessageIdRef.current,
      inFlightHumanMessagePersisted: inFlightHumanMessagePersistedRef.current,
      streamContext: streamContextRef.current,
    };
  };

  const persistActiveStreamSnapshot = (
    conversationId: string,
    nextState: InFlightStreamState
  ) => {
    const userId = recoveryUserIdRef.current;
    persistInFlightStream(userId, nextState);

    if (userId && generationIdRef.current && streamTenantIdRef.current) {
      upsertPendingLedger({
        generationId: generationIdRef.current,
        conversationId,
        userId,
        tenantId: streamTenantIdRef.current,
        lastChunkSeq: lastChunkSeqRef.current,
        phase: "streaming",
        streamContext: streamContextRef.current,
        inFlightHumanMessageId: inFlightHumanMessageIdRef.current,
        inFlightHumanMessagePersisted: inFlightHumanMessagePersistedRef.current,
      });
    }
  };

  const flushThrottledStreamPersist = () => {
    if (streamPersistTimeoutRef.current) {
      clearTimeout(streamPersistTimeoutRef.current);
      streamPersistTimeoutRef.current = null;
    }

    const conversationId = streamPersistConversationIdRef.current;
    if (!conversationId) {
      return;
    }

    const state = inFlightStreamsRef.current.get(conversationId);
    if (state) {
      persistActiveStreamSnapshot(conversationId, state);
    }
    streamPersistConversationIdRef.current = null;
  };

  const syncActiveStreamToMap = (
    conversationId: string,
    options?: { persist?: "immediate" | "throttled" }
  ) => {
    const nextState = buildActiveStreamState(conversationId);
    inFlightStreamsRef.current.set(conversationId, nextState);

    const persistMode = options?.persist ?? "immediate";
    if (persistMode === "throttled") {
      streamPersistConversationIdRef.current = conversationId;
      if (streamPersistTimeoutRef.current) {
        return;
      }

      streamPersistTimeoutRef.current = setTimeout(() => {
        streamPersistTimeoutRef.current = null;
        flushThrottledStreamPersist();
      }, STREAM_PERSIST_THROTTLE_MS);
      return;
    }

    if (
      streamPersistTimeoutRef.current &&
      streamPersistConversationIdRef.current === conversationId
    ) {
      clearTimeout(streamPersistTimeoutRef.current);
      streamPersistTimeoutRef.current = null;
      streamPersistConversationIdRef.current = null;
    }

    persistActiveStreamSnapshot(conversationId, nextState);
  };

  const flushPersistedInFlightStreams = () => {
    flushThrottledStreamPersist();

    if (activeStreamConversationIdRef.current) {
      syncActiveStreamToMap(activeStreamConversationIdRef.current, {
        persist: "immediate",
      });
      return;
    }

    persistInFlightStreamMap(
      recoveryUserIdRef.current,
      inFlightStreamsRef.current
    );
  };

  const applyInFlightStateToRefs = (state: InFlightStreamState) => {
    generationIdRef.current = state.generationId;
    lastChunkSeqRef.current = state.lastChunkSeq;
    streamHandoffDoneRef.current = state.streamHandoffDone;
    latestMessageRef.current = state.latestMessage;
    streamStateRef.current = { ...state.streamState };
    streamTextRef.current = state.streamText;
    referenceReplacementsRef.current = { ...state.referenceReplacements };
    typingBufferRef.current = state.typingBuffer;
    cotBufferRef.current = state.cotBuffer;
    revisedPromptBufferRef.current = state.revisedPromptBuffer;
    statusLongBufferRef.current = state.statusLongBuffer;
    queuedCharCountRef.current = state.queuedCharCount;
    queuedCotCountRef.current = state.queuedCotCount;
    queuedRevisedPromptCountRef.current = state.queuedRevisedPromptCount;
    queuedStatusLongCountRef.current = state.queuedStatusLongCount;
    pendingClosedMessageRef.current = state.pendingClosedMessage;
    inFlightHumanMessageIdRef.current = state.inFlightHumanMessageId;
    inFlightHumanMessagePersistedRef.current =
      state.inFlightHumanMessagePersisted;
    streamContextRef.current = state.streamContext;
    setLatestMessage(state.latestMessage);
    setWaitingForResponse(state.waitingForResponse);
  };

  const detachActiveStreamUi = () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    pendingClosedMessageRef.current = null;
    typingBufferRef.current = "";
    cotBufferRef.current = "";
    revisedPromptBufferRef.current = "";
    statusLongBufferRef.current = "";
    queuedCharCountRef.current = 0;
    queuedCotCountRef.current = 0;
    queuedRevisedPromptCountRef.current = 0;
    queuedStatusLongCountRef.current = 0;
    latestMessageRef.current = null;
    setLatestMessage(null);
    streamHandoffDoneRef.current = false;
    resetStreamState();
    generationIdRef.current = null;
    lastChunkSeqRef.current = 0;
    activeStreamConversationIdRef.current = null;
    streamContextRef.current = null;
  };

  const parkStreamForConversation = (conversationId: string) => {
    if (activeStreamConversationIdRef.current === conversationId) {
      syncActiveStreamToMap(conversationId, { persist: "immediate" });
    }
    streamControllerRef.current?.abort();
    streamControllerRef.current = null;
    if (selectedConversationRef.current?.id === conversationId) {
      detachActiveStreamUi();
    }
    if (activeStreamConversationIdRef.current === conversationId) {
      activeStreamConversationIdRef.current = null;
    }
  };

  const markStreamTerminal = (conversationId: string) => {
    const userId = recoveryUserIdRef.current;
    const state = inFlightStreamsRef.current.get(conversationId);
    if (state) {
      persistInFlightStream(userId, {
        ...state,
        isTerminal: true,
        waitingForResponse: false,
      });
    } else {
      clearPersistedInFlightStream(userId, conversationId);
    }
    if (activeStreamConversationIdRef.current === conversationId) {
      activeStreamConversationIdRef.current = null;
      generationIdRef.current = null;
    }
    inFlightStreamsRef.current.delete(conversationId);
  };

  const getLastHumanMessageText = (conversationId: string): string | null => {
    const conversation = selectedConversationRef.current;
    if (!conversation || conversation.id !== conversationId) {
      return null;
    }

    const messages = conversation.messages ?? [];
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.human) {
        return message.text;
      }
    }

    return null;
  };

  const ackStreamBuffer = async (generationId: string | null | undefined) => {
    if (!generationId) {
      return;
    }

    try {
      const token = await getCachedToken();
      await ackChatGeneration(generationId, token);
    } catch (error) {
      logger.error("Failed to ack stream generation buffer", {
        operation: "ackStreamBuffer",
        error,
        generationId,
      });
    }
  };

  const handleExpiredGeneration = async (conversationId: string) => {
    const state =
      inFlightStreamsRef.current.get(conversationId) ??
      getPersistedInFlightStream(recoveryUserId, conversationId);

    const streamContext = state?.streamContext;
    const languageCode =
      streamContext?.languageCode ?? selectedLanguage?.code ?? "da";
    const humanText =
      streamContext?.humanText ?? getLastHumanMessageText(conversationId) ?? "";
    const searchOptions =
      streamContext?.searchOptions ??
      selectedConversationRef.current?.searchOptions ??
      [];
    const filter =
      streamContext?.filter ??
      selectedConversationRef.current?.workspaceFilter ??
      [];

    if (state?.inFlightHumanMessagePersisted) {
      inFlightHumanMessagePersistedRef.current = true;
    }
    if (state?.inFlightHumanMessageId) {
      inFlightHumanMessageIdRef.current = state.inFlightHumanMessageId;
    }

    const generationIdToAck = state?.generationId ?? generationIdRef.current;
    markStreamTerminal(conversationId);
    detachActiveStreamUi();
    setWaitingForResponse(false);

    if (!humanText) {
      logger.warn("Could not resume a chat answer after a reload", {
        operation: "handleExpiredGeneration",
        conversationId,
        generationId: generationIdToAck,
      });
      showWarningToast("Svaret kunne ikke genoptages efter genindlæsning");
      await refreshConversationMessagesFromServer(conversationId);
      await ackStreamBuffer(generationIdToAck);
      clearRecoveryState(recoveryUserId, conversationId, generationIdToAck);
      return;
    }

    const refreshedMessages = await messageService.queryByConversationId({
      conversationId,
    });
    const messages = refreshedMessages.success ? refreshedMessages.value : [];
    const pendingEntry =
      recoveryUserId && generationIdToAck
        ? getPendingStream(recoveryUserId, generationIdToAck)
        : null;

    if (
      pendingEntry?.doneEvent &&
      isDoneEventAlreadyPersisted(messages, pendingEntry.doneEvent)
    ) {
      await refreshConversationMessagesFromServer(conversationId);
      await ackStreamBuffer(generationIdToAck);
      clearRecoveryState(recoveryUserId, conversationId, generationIdToAck);
      return;
    }

    if (
      isFailedStreamAlreadyPersisted(messages) ||
      isSuccessfulStreamAlreadyPersisted(messages)
    ) {
      await refreshConversationMessagesFromServer(conversationId);
      await ackStreamBuffer(generationIdToAck);
      clearRecoveryState(recoveryUserId, conversationId, generationIdToAck);
      return;
    }

    await persistFailedStream(
      getGenerationErrorText(languageCode),
      conversationId,
      humanText,
      languageCode,
      searchOptions,
      filter,
      streamContext?.workspaceDocuments,
      generationIdToAck
    );
  };

  const persistConversationTitle = (conversationId: string, title: string) =>
    saveConversationTitle(titleWritesRef.current, conversationId, title, {
      update: (id, nextTitle) =>
        conversationService.update({ id, title: nextTitle }),
      onFailure: (id, error) =>
        logger.error("Failed to save conversation title", {
          operation: "persistConversationTitle",
          error,
          conversationId: id,
        }),
    });

  const buildStreamCallbacks = (): ChatStreamCallbacks => {
    const context = streamContextRef.current;
    if (!context) {
      throw new Error("Stream context is not initialized");
    }

    const {
      conversationId,
      languageCode,
      humanText,
      searchOptions,
      filter,
      workspaceDocuments,
      documentWorkspaceMap,
      isRetry,
    } = context;

    return {
      onTitle: async (title) => {
        if (isRetry) return;
        onUpdateTitleConversation({ id: conversationId, title });
        await persistConversationTitle(conversationId, title);
      },
      onChunk: async (chunk) => {
        streamStateRef.current.statusShort += chunk.statusShort ?? "";
        streamStateRef.current.statusLong += chunk.statusLong ?? "";

        if (chunk.chainOfThought) {
          streamStateRef.current.chainOfThoughtMessage += chunk.text ?? "";
          streamStateRef.current.revisedPrompt += chunk.rationale ?? "";
        } else {
          const nextRawText = `${streamTextRef.current}${chunk.text ?? ""}`;
          const processed = processReferences({
            text: nextRawText,
            references:
              chunk.references?.length > 0
                ? chunk.references
                : streamStateRef.current.references,
            replacements: referenceReplacementsRef.current,
            documentWorkspaceMap,
          });

          referenceReplacementsRef.current = processed.replacements;
          streamTextRef.current = nextRawText;
          streamStateRef.current.text = processed.text;
          streamStateRef.current.references = processed.references;
        }

        if (chunk.lemmatizedText) {
          try {
            streamStateRef.current.lemmatizedText = JSON.parse(
              chunk.lemmatizedText
            ) as Record<string, string>;
          } catch {
            streamStateRef.current.lemmatizedText = {};
          }
        }

        onUpdateMessage({
          conversationId,
          id: TEMP_AI_MESSAGE_ID,
          human: false,
          text: streamStateRef.current.text,
          references: streamStateRef.current.references,
          language: languageCode,
          chainOfThoughtMessage: streamStateRef.current.chainOfThoughtMessage,
          revisedPrompt: streamStateRef.current.revisedPrompt,
          lemmatizedText: streamStateRef.current.lemmatizedText,
          isClosed: false,
          statusShort: streamStateRef.current.statusShort,
          statusLong: streamStateRef.current.statusLong,
        });
        syncActiveStreamToMap(conversationId, { persist: "throttled" });
      },
      onDone: async (doneEvent) => {
        const finalDoneEvent: ChatDoneEvent = {
          ...doneEvent,
          text: streamStateRef.current.text,
          references: streamStateRef.current.references,
          chainOfThoughtMessage: streamStateRef.current.chainOfThoughtMessage,
          revisedPrompt: streamStateRef.current.revisedPrompt,
          lemmatizedText: streamStateRef.current.lemmatizedText,
          statusShort: streamStateRef.current.statusShort,
          statusLong: streamStateRef.current.statusLong,
        };

        onUpdateMessage({
          conversationId,
          id: TEMP_AI_MESSAGE_ID,
          human: false,
          text: finalDoneEvent.text,
          references: finalDoneEvent.references,
          language: languageCode,
          chainOfThoughtMessage: finalDoneEvent.chainOfThoughtMessage,
          revisedPrompt: finalDoneEvent.revisedPrompt,
          lemmatizedText: finalDoneEvent.lemmatizedText,
          isClosed: true,
          statusShort: finalDoneEvent.statusShort,
          statusLong: finalDoneEvent.statusLong,
        });

        const generationIdToAck = generationIdRef.current;
        if (recoveryUserId && generationIdToAck && streamTenantIdRef.current) {
          upsertPendingLedger({
            generationId: generationIdToAck,
            conversationId,
            userId: recoveryUserId,
            tenantId: streamTenantIdRef.current,
            lastChunkSeq: lastChunkSeqRef.current,
            phase: "persisting_done",
            streamContext: streamContextRef.current,
            doneEvent: finalDoneEvent,
            inFlightHumanMessageId: inFlightHumanMessageIdRef.current,
            inFlightHumanMessagePersisted:
              inFlightHumanMessagePersistedRef.current,
          });
        }
        markStreamTerminal(conversationId);
        await finalizeStreamPersistence(
          finalDoneEvent,
          conversationId,
          generationIdToAck,
          languageCode
        );
      },
      onError: async (errorMessage) => {
        if (isStreamAbortError(errorMessage)) {
          return;
        }

        if (isExpiredGenerationError(errorMessage)) {
          await handleExpiredGeneration(conversationId);
          return;
        }

        if (isRetryableTransportStreamError(errorMessage)) {
          const state = inFlightStreamsRef.current.get(conversationId);
          if (state && !state.isTerminal && generationIdRef.current) {
            try {
              const token = await getCachedToken();
              streamControllerRef.current = subscribeChatGeneration({
                generationId: generationIdRef.current,
                afterSeq: lastChunkSeqRef.current,
                callbacks: buildStreamCallbacks(),
                token,
                onSeq: (seq) => {
                  lastChunkSeqRef.current = seq;
                  syncActiveStreamToMap(conversationId, {
                    persist: "throttled",
                  });
                },
              });
              return;
            } catch {
              // fall through to persist failed stream
            }
          }
        }

        const generationIdToAck = generationIdRef.current;
        const persistableError = resolvePersistableStreamError(
          errorMessage,
          languageCode
        );
        if (recoveryUserId && generationIdToAck && streamTenantIdRef.current) {
          upsertPendingLedger({
            generationId: generationIdToAck,
            conversationId,
            userId: recoveryUserId,
            tenantId: streamTenantIdRef.current,
            lastChunkSeq: lastChunkSeqRef.current,
            phase: "persisting_error",
            streamContext: streamContextRef.current,
            errorMessage: persistableError,
            inFlightHumanMessageId: inFlightHumanMessageIdRef.current,
            inFlightHumanMessagePersisted:
              inFlightHumanMessagePersistedRef.current,
          });
        }
        markStreamTerminal(conversationId);
        resetStreamingRefsOnly();
        setWaitingForResponse(false);
        showFailedStreamInConversation(
          conversationId,
          persistableError,
          languageCode
        );
        await persistFailedStream(
          persistableError,
          conversationId,
          humanText,
          languageCode,
          searchOptions,
          filter,
          workspaceDocuments,
          generationIdToAck
        );
      },
    };
  };

  const attachStreamSubscription = async (state: InFlightStreamState) => {
    const token = await getCachedToken();
    streamControllerRef.current = subscribeChatGeneration({
      generationId: state.generationId,
      afterSeq: state.lastChunkSeq,
      callbacks: buildStreamCallbacks(),
      token,
      onSeq: (seq) => {
        lastChunkSeqRef.current = seq;
        syncActiveStreamToMap(state.conversationId, { persist: "throttled" });
      },
    });
  };

  const restoreStreamForConversation = async (conversationId: string) => {
    const persisted = getPersistedInFlightStream(
      recoveryUserId,
      conversationId
    );
    if (persisted) {
      inFlightStreamsRef.current.set(conversationId, persisted);
    }

    const state = inFlightStreamsRef.current.get(conversationId);
    if (!state || state.isTerminal) {
      detachActiveStreamUi();
      setWaitingForResponse(false);
      return;
    }

    const synthesizedLatest = synthesizeLatestMessageFromStreamState(state);
    const hydratedState: InFlightStreamState = {
      ...state,
      latestMessage: synthesizedLatest ?? state.latestMessage,
      streamHandoffDone: synthesizedLatest ? true : state.streamHandoffDone,
    };
    inFlightStreamsRef.current.set(conversationId, hydratedState);

    if (!hydratedState.streamContext) {
      logger.warn(
        "Skipping stream restore — persisted in-flight state has no stream context",
        {
          operation: "restoreStreamForConversation",
          conversationId,
          generationId: hydratedState.generationId,
        }
      );
      markStreamTerminal(conversationId);
      detachActiveStreamUi();
      setWaitingForResponse(false);
      return;
    }

    applyInFlightStateToRefs(hydratedState);
    activeStreamConversationIdRef.current = conversationId;
    setWaitingForResponse(true);

    if (hydratedState.latestMessage) {
      const hasTypingBacklog =
        typingBufferRef.current.length > 0 ||
        cotBufferRef.current.length > 0 ||
        revisedPromptBufferRef.current.length > 0 ||
        statusLongBufferRef.current.length > 0;

      if (hasTypingBacklog) {
        startTypingInterval(hydratedState.latestMessage);
      }
    }

    await attachStreamSubscription(hydratedState);
  };

  const resetStreamingRefsOnly = () => {
    streamControllerRef.current?.abort();
    streamControllerRef.current = null;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    pendingClosedMessageRef.current = null;
    typingBufferRef.current = "";
    cotBufferRef.current = "";
    revisedPromptBufferRef.current = "";
    statusLongBufferRef.current = "";
    queuedCharCountRef.current = 0;
    queuedCotCountRef.current = 0;
    queuedRevisedPromptCountRef.current = 0;
    queuedStatusLongCountRef.current = 0;
    latestMessageRef.current = null;
    setLatestMessage(null);
    streamHandoffDoneRef.current = false;
    streamContextRef.current = null;
    resetStreamState();
  };

  const showFailedStreamInConversation = (
    conversationId: string,
    errorMessage: string,
    languageCode: string
  ) => {
    const failedAiMessage: Message = {
      ...TEMP_AI_MESSAGE,
      id: TEMP_AI_MESSAGE_ID,
      conversationId,
      human: false,
      text: errorMessage,
      statusShort: GENERATION_FAILED_STATUS,
      isClosed: true,
      language: languageCode,
    };

    const replaceStreamingAiWithFailed = (messages: Message[]) => [
      ...messages.filter((message) => message.id !== TEMP_AI_MESSAGE_ID),
      failedAiMessage,
    ];

    setSelectedConversation((prevSelected) =>
      prevSelected && prevSelected.id === conversationId
        ? {
            ...prevSelected,
            messages: replaceStreamingAiWithFailed(prevSelected.messages ?? []),
          }
        : prevSelected
    );

    setConversations((prevConversations) =>
      prevConversations.map((conversation) =>
        conversation.id === conversationId
          ? {
              ...conversation,
              messages: replaceStreamingAiWithFailed(
                conversation.messages ?? []
              ),
            }
          : conversation
      )
    );
  };

  const clearInFlightStreamingUi = () => {
    resetStreamingRefsOnly();

    setSelectedConversation((prevSelected) => {
      if (
        !prevSelected?.messages?.some(
          (message) =>
            message.id === TEMP_AI_MESSAGE_ID ||
            message.id === TEMP_HUMAN_MESSAGE_ID
        )
      ) {
        return prevSelected;
      }

      return {
        ...prevSelected,
        messages: prevSelected.messages.filter(
          (message) =>
            message.id !== TEMP_AI_MESSAGE_ID &&
            message.id !== TEMP_HUMAN_MESSAGE_ID
        ),
      };
    });

    setConversations((prevConversations) =>
      prevConversations.map((conversation) => {
        if (
          !conversation.messages?.some(
            (message) =>
              message.id === TEMP_AI_MESSAGE_ID ||
              message.id === TEMP_HUMAN_MESSAGE_ID
          )
        ) {
          return conversation;
        }

        return {
          ...conversation,
          messages: conversation.messages.filter(
            (message) =>
              message.id !== TEMP_AI_MESSAGE_ID &&
              message.id !== TEMP_HUMAN_MESSAGE_ID
          ),
        };
      })
    );
  };

  const clearInFlightHumanMessageTracking = () => {
    inFlightHumanMessageIdRef.current = null;
    inFlightHumanMessagePersistedRef.current = false;
  };

  const applyRefreshedMessages = (
    conversationId: string,
    messages: Message[]
  ) => {
    resetStreamingRefsOnly();
    setWaitingForResponse(false);
    clearInFlightHumanMessageTracking();

    const sortedMessages = [...messages].sort(
      (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)
    );

    setSelectedConversation((prevSelected) =>
      prevSelected && prevSelected.id === conversationId
        ? {
            ...prevSelected,
            messages: sortedMessages,
          }
        : prevSelected
    );

    setConversations((prevConversations) =>
      prevConversations.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, messages: sortedMessages }
          : conversation
      )
    );
  };

  const refreshConversationMessagesFromServer = async (
    conversationId: string
  ) => {
    const refreshedMessages = await messageService.queryByConversationId({
      conversationId,
    });

    if (refreshedMessages.success) {
      applyRefreshedMessages(conversationId, refreshedMessages.value);
      return;
    }

    logger.error("Failed to refresh conversation messages", {
      operation: "refreshConversationMessagesFromServer",
      conversationId,
    });
    clearInFlightStreamingUi();
    clearInFlightHumanMessageTracking();
    setWaitingForResponse(false);
    showErrorToastWithAction(
      "Beskederne kunne ikke hentes",
      () => void refreshConversationMessagesFromServer(conversationId)
    );
  };

  // LAR-543: a generation whose answer never saves is replayed on every
  // visit for as long as chat-api keeps it. After a few failed saves the
  // failed-answer message takes its place, with the chat's usual retry.
  const handlePersistFailure = async (
    conversationId: string,
    generationId: string | null | undefined,
    languageCode: string | undefined
  ): Promise<"retry" | "replaced"> => {
    const userId = recoveryUserId;
    if (!generationId || !userId) {
      return "retry";
    }

    const language = languageCode ?? selectedLanguage?.code ?? "da";
    return handleStreamPersistFailure({
      userId,
      conversationId,
      generationId,
      saveFailedAnswer: async () => {
        const result = await messageService.saveCompletedMessage({
          conversationId,
          human: false,
          text: getGenerationErrorText(language),
          references: [],
          language,
          statusShort: GENERATION_FAILED_STATUS,
          isClosed: true,
        });
        return result.success;
      },
      release: async () => {
        await refreshConversationMessagesFromServer(conversationId);
        await ackStreamBuffer(generationId);
        clearRecoveryState(userId, conversationId, generationId);
      },
    });
  };

  const finalizeStreamPersistence = async (
    doneEvent: ChatDoneEvent,
    conversationId: string,
    generationId?: string | null,
    languageCode?: string
  ) => {
    if (generationId && !tryAcquirePersistGenerationLock(generationId)) {
      return;
    }

    try {
      const refreshedMessages = await messageService.queryByConversationId({
        conversationId,
      });
      const existingMessages = refreshedMessages.success
        ? refreshedMessages.value
        : [];
      if (isDoneEventAlreadyPersisted(existingMessages, doneEvent)) {
        await ackStreamBuffer(generationId);
        clearRecoveryState(recoveryUserId, conversationId, generationId);
        return;
      }

      const saveResult = await messageService.saveCompletedMessage({
        conversationId,
        human: false,
        text: doneEvent.text,
        references: doneEvent.references,
        language: languageCode ?? selectedLanguage?.code ?? "da",
        chainOfThought: useChainOfThought,
        chainOfThoughtMessage: doneEvent.chainOfThoughtMessage,
        revisedPrompt: doneEvent.revisedPrompt,
        lemmatizedText: doneEvent.lemmatizedText,
        statusShort: doneEvent.statusShort,
        statusLong: doneEvent.statusLong,
        isClosed: true,
      });

      if (!saveResult.success) {
        throw restError(saveResult, "Failed to persist completed message");
      }

      await refreshConversationMessagesFromServer(conversationId);
      await ackStreamBuffer(generationId);
      clearRecoveryState(recoveryUserId, conversationId, generationId);
    } catch (error) {
      logger.error("Failed to save the completed chat answer", {
        operation: "finalizeStreamPersistence",
        error,
        conversationId,
        generationId,
      });
      const outcome = await handlePersistFailure(
        conversationId,
        generationId,
        languageCode
      );
      if (outcome === "replaced") {
        return;
      }
      showErrorToastWithAction("Beskeden kunne ikke gemmes", () =>
        finalizeStreamPersistence(
          doneEvent,
          conversationId,
          generationId,
          languageCode
        )
      );
    } finally {
      if (generationId) {
        releasePersistGenerationLock(generationId);
      }
    }
  };

  type FailedStreamHumanTracking = {
    inFlightHumanMessageId: string | null;
    inFlightHumanMessagePersisted: boolean;
  };

  const persistFailedStream = async (
    errorMessage: string,
    conversationId: string,
    humanText: string,
    languageCode: string,
    searchOptions: SearchOption[],
    filter: SendMessageContextFilter[],
    workspaceDocuments?: ConversationWorkspaceDocumentSnapshot[],
    generationId?: string | null,
    humanTracking?: FailedStreamHumanTracking
  ) => {
    if (generationId && !tryAcquirePersistGenerationLock(generationId)) {
      return;
    }

    try {
      const refreshedMessages = await messageService.queryByConversationId({
        conversationId,
      });
      const existingMessages = refreshedMessages.success
        ? refreshedMessages.value
        : [];
      if (isFailedStreamAlreadyPersisted(existingMessages)) {
        await ackStreamBuffer(generationId);
        clearRecoveryState(recoveryUserId, conversationId, generationId);
        return;
      }

      const humanAlreadyPersisted =
        humanTracking?.inFlightHumanMessagePersisted ??
        inFlightHumanMessagePersistedRef.current;
      if (!humanAlreadyPersisted) {
        const humanMessageId =
          humanTracking?.inFlightHumanMessageId ??
          inFlightHumanMessageIdRef.current ??
          uuidv4();
        const humanResult = await messageService.saveCompletedMessage({
          id: humanMessageId,
          conversationId,
          human: true,
          text: humanText,
          references: [],
          language: languageCode,
          isClosed: true,
        });
        if (!humanResult.success) {
          throw restError(humanResult, "Failed to persist human message");
        }
        if (!humanTracking) {
          inFlightHumanMessagePersistedRef.current = true;
        }
      }

      const failedAiResult = await messageService.saveCompletedMessage({
        conversationId,
        human: false,
        text: errorMessage,
        references: [],
        language: languageCode,
        statusShort: GENERATION_FAILED_STATUS,
        isClosed: true,
      });

      if (!failedAiResult.success) {
        throw restError(failedAiResult, "Failed to persist error response");
      }

      await refreshConversationMessagesFromServer(conversationId);
      await ackStreamBuffer(generationId);
      clearRecoveryState(recoveryUserId, conversationId, generationId);
    } catch (error) {
      logger.error("Failed to persist failed stream messages", {
        operation: "persistFailedStream",
        error,
        conversationId,
      });
      clearInFlightStreamingUi();
      clearInFlightHumanMessageTracking();
      setWaitingForResponse(false);
      const outcome = await handlePersistFailure(
        conversationId,
        generationId,
        languageCode
      );
      if (outcome === "replaced") {
        return;
      }
      await refreshConversationMessagesFromServer(conversationId);
      showErrorToastWithAction(
        "Beskeden kunne ikke gemmes",
        () =>
          void persistFailedStream(
            errorMessage,
            conversationId,
            humanText,
            languageCode,
            searchOptions,
            filter,
            workspaceDocuments,
            generationId
          )
      );
    } finally {
      if (generationId) {
        releasePersistGenerationLock(generationId);
      }
    }
  };

  const reconcileSinglePendingEntry = async (
    entry: PendingStreamRecovery,
    token: string
  ) => {
    if (!recoveryUserId || entry.userId !== recoveryUserId) {
      return;
    }

    if (generationIdRef.current === entry.generationId) {
      return;
    }

    if (!tryAcquireReconcileGenerationLock(entry.generationId)) {
      return;
    }

    let releaseReconcileLockInFinally = true;

    try {
      const refreshedMessages = await messageService.queryByConversationId({
        conversationId: entry.conversationId,
      });
      const messages = refreshedMessages.success ? refreshedMessages.value : [];

      if (entry.phase === "persisting_done" && entry.doneEvent) {
        if (isDoneEventAlreadyPersisted(messages, entry.doneEvent)) {
          await ackChatGeneration(entry.generationId, token);
          clearRecoveryState(
            entry.userId,
            entry.conversationId,
            entry.generationId
          );
          return;
        }

        await finalizeStreamPersistence(
          entry.doneEvent,
          entry.conversationId,
          entry.generationId,
          entry.streamContext?.languageCode
        );
        return;
      }

      if (
        entry.phase === "persisting_error" &&
        entry.errorMessage &&
        entry.streamContext
      ) {
        if (isFailedStreamAlreadyPersisted(messages)) {
          await ackChatGeneration(entry.generationId, token);
          clearRecoveryState(
            entry.userId,
            entry.conversationId,
            entry.generationId
          );
          return;
        }

        await persistFailedStream(
          entry.errorMessage,
          entry.conversationId,
          entry.streamContext.humanText,
          entry.streamContext.languageCode,
          entry.streamContext.searchOptions,
          entry.streamContext.filter,
          entry.streamContext.workspaceDocuments,
          entry.generationId,
          {
            inFlightHumanMessageId: entry.inFlightHumanMessageId,
            inFlightHumanMessagePersisted: entry.inFlightHumanMessagePersisted,
          }
        );
        return;
      }

      // Streaming recoveries subscribe in the background so exclusive
      // reconcile can process other pending entries without blocking.
      releaseReconcileLockInFinally = false;
      void (async () => {
        try {
          await new Promise<void>((resolve) => {
            subscribeChatGeneration({
              generationId: entry.generationId,
              afterSeq: entry.lastChunkSeq,
              token,
              callbacks: {
                onTitle: async () => undefined,
                onChunk: async () => undefined,
                onDone: async (doneEvent) => {
                  const latestMessages =
                    await messageService.queryByConversationId({
                      conversationId: entry.conversationId,
                    });
                  const messagesForCheck = latestMessages.success
                    ? latestMessages.value
                    : messages;
                  const alreadyPersisted = isDoneEventAlreadyPersisted(
                    messagesForCheck,
                    doneEvent
                  );
                  if (alreadyPersisted) {
                    await ackChatGeneration(entry.generationId, token);
                    clearRecoveryState(
                      entry.userId,
                      entry.conversationId,
                      entry.generationId
                    );
                    resolve();
                    return;
                  }

                  let persistableDoneEvent = doneEvent;
                  const streamContext = entry.streamContext;
                  if (streamContext) {
                    const processed = processReferences({
                      text: doneEvent.text,
                      references: doneEvent.references ?? [],
                      replacements: {},
                      documentWorkspaceMap: streamContext.documentWorkspaceMap,
                    });
                    persistableDoneEvent = {
                      ...doneEvent,
                      text: processed.text,
                      references: processed.references,
                    };
                  }

                  await finalizeStreamPersistence(
                    persistableDoneEvent,
                    entry.conversationId,
                    entry.generationId,
                    streamContext?.languageCode
                  );
                  resolve();
                },
                onError: async (errorMessage) => {
                  if (isExpiredGenerationError(errorMessage)) {
                    await ackChatGeneration(entry.generationId, token);
                    clearRecoveryState(
                      entry.userId,
                      entry.conversationId,
                      entry.generationId
                    );
                    resolve();
                    return;
                  }

                  const context = entry.streamContext;
                  if (!context) {
                    if (!isFailedStreamAlreadyPersisted(messages)) {
                      const serverOnlyLanguageCode = (() => {
                        for (
                          let index = messages.length - 1;
                          index >= 0;
                          index -= 1
                        ) {
                          const message = messages[index];
                          if (message.human && message.language) {
                            return message.language;
                          }
                        }
                        return selectedLanguage?.code ?? "da";
                      })();
                      const persistableError = resolvePersistableStreamError(
                        errorMessage,
                        serverOnlyLanguageCode
                      );
                      const failedAiResult =
                        await messageService.saveCompletedMessage({
                          conversationId: entry.conversationId,
                          human: false,
                          text: persistableError,
                          references: [],
                          language: serverOnlyLanguageCode,
                          statusShort: GENERATION_FAILED_STATUS,
                          isClosed: true,
                        });
                      if (!failedAiResult.success) {
                        logger.error(
                          "Failed to persist server-only stream error",
                          {
                            operation: "reconcileSinglePendingEntry",
                            conversationId: entry.conversationId,
                            generationId: entry.generationId,
                          }
                        );
                        resolve();
                        return;
                      }
                      await refreshConversationMessagesFromServer(
                        entry.conversationId
                      );
                    }

                    await ackChatGeneration(entry.generationId, token);
                    clearRecoveryState(
                      entry.userId,
                      entry.conversationId,
                      entry.generationId
                    );
                    resolve();
                    return;
                  }

                  if (isFailedStreamAlreadyPersisted(messages)) {
                    await ackChatGeneration(entry.generationId, token);
                    clearRecoveryState(
                      entry.userId,
                      entry.conversationId,
                      entry.generationId
                    );
                    resolve();
                    return;
                  }

                  await persistFailedStream(
                    resolvePersistableStreamError(
                      errorMessage,
                      context.languageCode
                    ),
                    entry.conversationId,
                    context.humanText,
                    context.languageCode,
                    context.searchOptions,
                    context.filter,
                    context.workspaceDocuments,
                    entry.generationId,
                    {
                      inFlightHumanMessageId: entry.inFlightHumanMessageId,
                      inFlightHumanMessagePersisted:
                        entry.inFlightHumanMessagePersisted,
                    }
                  );
                  resolve();
                },
              },
            });
          });
        } catch (error) {
          logger.error("Failed to reconcile streaming pending entry", {
            operation: "reconcileSinglePendingEntry",
            error,
            generationId: entry.generationId,
            conversationId: entry.conversationId,
          });
        } finally {
          releaseReconcileGenerationLock(entry.generationId);
        }
      })();
    } finally {
      if (releaseReconcileLockInFinally) {
        releaseReconcileGenerationLock(entry.generationId);
      }
    }
  };

  const reconcilePendingStreams = async (): Promise<void> => {
    if (!recoveryUserId) {
      return;
    }

    return runExclusiveReconcile(async () => {
      const token = await getCachedToken();
      const tenantId = parseTenantIdFromAccessToken(token);
      if (!tenantId) {
        logger.warn(
          "Skipping pending stream reconciliation — tenant id missing from access token",
          { operation: "reconcilePendingStreams", userId: recoveryUserId }
        );
        return;
      }

      let serverPending: Awaited<ReturnType<typeof listPendingGenerations>> =
        [];
      try {
        serverPending = await listPendingGenerations(token);
      } catch (error) {
        logger.error("Failed to list pending stream generations", {
          operation: "reconcilePendingStreams",
          error,
          userId: recoveryUserId,
        });
      }

      const merged = mergePendingRecoveries(
        listPendingStreams(recoveryUserId),
        serverPending,
        recoveryUserId,
        tenantId
      );

      for (const entry of merged) {
        const reconcileEntry = async () => {
          try {
            await reconcileSinglePendingEntry(entry, token);
          } catch (error) {
            logger.error("Failed to reconcile pending stream", {
              operation: "reconcilePendingStreams",
              error,
              generationId: entry.generationId,
              conversationId: entry.conversationId,
            });
          }
        };

        if (entry.phase === "streaming") {
          void reconcileEntry();
        } else {
          await reconcileEntry();
        }
      }
    });
  };

  useEffect(() => {
    if (!isAuthenticated || !recoveryUserId) {
      return;
    }

    void reconcilePendingStreams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, recoveryUserId]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onUpdateMessage = (rawPayload: any) => {
    const payload: Message = camelcaseKeys(rawPayload, { deep: true });

    const fullCot = payload.chainOfThoughtMessage ?? "";
    const fullRevisedPrompt = payload.revisedPrompt ?? "";
    const fullStatusLong = payload.statusLong ?? "";
    const fullText = payload.text ?? "";

    // —— chain‑of‑thought diff —— //
    if (fullCot.length > queuedCotCountRef.current) {
      const diffCot = fullCot.slice(queuedCotCountRef.current);
      cotBufferRef.current += diffCot;
      queuedCotCountRef.current = fullCot.length;
    }

    // —— revised prompt diff —— //
    if (fullRevisedPrompt.length > queuedRevisedPromptCountRef.current) {
      const diffRevisedPrompt = fullRevisedPrompt.slice(
        queuedRevisedPromptCountRef.current
      );
      revisedPromptBufferRef.current += diffRevisedPrompt;
      queuedRevisedPromptCountRef.current = fullRevisedPrompt.length;
    }

    // —— status long diff —— //
    if (fullStatusLong.length > queuedStatusLongCountRef.current) {
      const diffStatusLong = fullStatusLong.slice(
        queuedStatusLongCountRef.current
      );
      statusLongBufferRef.current += diffStatusLong;
      queuedStatusLongCountRef.current = fullStatusLong.length;
    }

    // —— answer text diff —— //
    if (fullText.length > queuedCharCountRef.current) {
      const diffText = fullText.slice(queuedCharCountRef.current);
      typingBufferRef.current += diffText;
      queuedCharCountRef.current = fullText.length;
    }

    if (!streamHandoffDoneRef.current) {
      streamHandoffDoneRef.current = true;

      const shellMessage: Message = {
        ...payload,
        text: "",
        chainOfThoughtMessage: "",
        revisedPrompt: "",
        statusLong: "",
      };

      latestMessageRef.current = shellMessage;
      setLatestMessage(shellMessage);

      const filterTempAiMessages = (messages: Message[]) =>
        messages
          .filter(
            (message) =>
              message.id !== TEMP_AI_MESSAGE_ID && message.id !== payload.id
          )
          .sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );

      setSelectedConversation((prevSelected) => {
        if (!prevSelected || prevSelected.id !== payload.conversationId) {
          return prevSelected;
        }
        return {
          ...prevSelected,
          messages: filterTempAiMessages(prevSelected.messages || []),
        };
      });

      setConversations((prevConversations) =>
        prevConversations.map((conversation) =>
          conversation.id === payload.conversationId
            ? {
                ...conversation,
                messages: filterTempAiMessages(conversation.messages || []),
              }
            : conversation
        )
      );
    }

    // ——— incremental reference updates ———
    // Don't update the document reference store if the user is viewing a reference page
    // This prevents the content from disappearing when clicking on a reference while a new message is streaming
    if (!isOnReferencePage()) {
      if (payload.references?.length) {
        // Only update if the payload contains *new* references we haven't shown yet
        const currentLength = latestMessageRef.current?.references?.length ?? 0;

        if (payload.references.length > currentLength) {
          // 1️⃣ push them to the reference sidebar immediately
          setDocumentReferences(payload.references);

          // 2️⃣ keep the in‑flight message in sync so it re‑renders with the new refs
          if (latestMessageRef.current) {
            latestMessageRef.current = {
              ...latestMessageRef.current,
              references: payload.references,
            };
            setLatestMessage(latestMessageRef.current);
          }
        }
      } else {
        reset();
      }
    } else {
      // Still update the latestMessage's references even when on a reference page
      // so that when the user navigates back to chat, the references are available
      if (latestMessageRef.current && payload.references) {
        latestMessageRef.current = {
          ...latestMessageRef.current,
          references: payload.references,
        };
        setLatestMessage(latestMessageRef.current);
      }
    }

    /* ——— statusShort (no streaming) ——— */
    if (payload.statusShort) {
      if (latestMessageRef.current) {
        latestMessageRef.current = {
          ...latestMessageRef.current,
          statusShort: payload.statusShort,
        };
        setLatestMessage(latestMessageRef.current);
      }
    }

    startTypingInterval(payload);

    if (payload.isClosed) pendingClosedMessageRef.current = payload;
  };

  /* __________________Utils__________________ */
  const deleteConversation = async (conversation: Conversation) => {
    if (selectedConversation?.id === conversation.id)
      setSelectedConversation(undefined);

    setConversations(conversations.filter((c) => c.id !== conversation.id));

    await conversationService.delete(conversation.id);
  };

  const rateMessage = async (message: Message, rating: MessageRating) => {
    if (!selectedConversation?.id) return;
    message.rating = rating;
    await messageService.rateMessage({
      id: message.id,
      conversationId: selectedConversation.id,
      rating: rating,
    });
    setSelectedConversation((prevSelected) =>
      prevSelected && prevSelected.messages
        ? {
            ...prevSelected,
            messages: prevSelected.messages.map((message) =>
              message.id === message.id
                ? {
                    ...message,
                    rating: message.rating,
                  }
                : message
            ),
          }
        : prevSelected
    );
  };

  const selectConversation = async (conversation: Conversation | undefined) => {
    const runId = ++selectConversationRunIdRef.current;
    const isStaleSelection = () => runId !== selectConversationRunIdRef.current;

    const leavingConversationId = selectedConversationRef.current?.id;
    if (leavingConversationId && leavingConversationId !== conversation?.id) {
      parkStreamForConversation(leavingConversationId);
      saveChatScrollStateFromDom(leavingConversationId);
    }

    if (!conversation) {
      if (isStaleSelection()) {
        return;
      }
      detachActiveStreamUi();
      setWaitingForResponse(false);
      // This run is the newest one, and it is loading nothing. A selection it
      // overtook returns at its own staleness check without clearing the flag,
      // so without this an interrupted open leaves the chat under a spinner
      // that never resolves (LAR-405).
      setIsLoadingMessages({ isLoading: false, conversationId: null });
      return setSelectedConversation(undefined);
    }
    try {
      setIsLoadingMessages({
        isLoading: true,
        conversationId: conversation.id,
      });
      const fetchedMessages = await messageService.queryByConversationId({
        conversationId: conversation.id,
      });

      if (isStaleSelection()) {
        return;
      }

      if (!fetchedMessages.success) {
        throw restError(fetchedMessages, "Failed to fetch messages");
      }

      conversation.messages = fetchedMessages.value ?? [];

      if (conversation.messages) {
        const lastMessageNotHuman = conversation.messages
          .filter((message) => !message.human)
          .sort((a, b) => {
            return (
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            );
          })
          .pop();

        if (lastMessageNotHuman) {
          setDocumentReferences(lastMessageNotHuman.references);
        } else {
          reset();
        }
      }

      conversation.messages = conversation.messages.sort((a, b) => {
        return Date.parse(a.createdAt) - Date.parse(b.createdAt);
      });
      const source = convertSearchOptionsToSource(conversation.searchOptions);
      const workspaceFilter = convertContextFiltersToWorkspaceContext(
        conversation.workspaceFilter
      );

      // Clear filters when switching to an existing conversation
      // The conversation's source will be set from its searchOptions
      resetFilters();
      setSelectedSource(source);
      // Also populate the filters object so the UI can display the selected source
      if (source) {
        setFilters(source, {});
      }
      setWorkspaceContext(workspaceFilter);

      setSelectedConversation(conversation);
      setIsLoadingMessages({ isLoading: false, conversationId: null });
      try {
        await restoreStreamForConversation(conversation.id);
      } catch (error) {
        logger.warn("Stream restoration failed during conversation selection", {
          operation: "selectConversation",
          conversationId: conversation.id,
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      }

      if (isStaleSelection()) {
        return;
      }
    } catch (error) {
      if (isStaleSelection()) {
        return;
      }
      logger.error("Failed to load messages for conversation", {
        operation: "selectConversation",
        error,
        conversationId: conversation.id,
      });
      setIsLoadingMessages({ isLoading: false, conversationId: null });
      // Rethrown as it is: a fresh "Failed to fetch messages" dropped the status
      // and the stack (a failed fetch above already throws with that message).
      throw error;
    }
  };

  const selectConversationById = async (conversationId: string) => {
    // Claim the loading state before the first await. Opening a conversation the
    // list has not seen costs two round trips before `selectConversation` marks
    // it loading, and the chat view spends them rendering its "new chat"
    // greeting over a conversation that is on its way in (LAR-405).
    setIsLoadingMessages({ isLoading: true, conversationId });

    try {
      const conversationIndex = conversations.findIndex(
        (c) => c.id === conversationId
      );

      // If the conversation is not fetched in cache yet, we will fetch it from the server
      if (conversationIndex === -1) {
        const fetchedConversation =
          await conversationService.getConversationById(
            conversationId,
            pageSize
          );
        if (!fetchedConversation.success) {
          throw restError(fetchedConversation, "Failed to fetch conversation");
        }

        const pageNumber = fetchedConversation.value.pageNumber;
        setConversations([...conversations, fetchedConversation.value]);
        setPageNumber(pageNumber);

        await fetchConversations(pageNumber);
        await selectConversation(fetchedConversation.value);
        return;
      }

      const conversation = conversations[conversationIndex];

      // For conversations already in the list, we still calculate the page number
      const pageNumber = Math.floor(conversationIndex / pageSize) + 1;
      setPageNumber(pageNumber);

      await selectConversation(conversation);
    } catch (error) {
      logger.warn("Failed to open conversation by id", {
        operation: "selectConversationById",
        conversationId,
        errorMessage: error instanceof Error ? error.message : String(error),
      });
      // Only release the flag if it is still ours: a newer selection may already
      // have claimed it, and clearing that one would strand its spinner.
      setIsLoadingMessages((current) =>
        current.conversationId === conversationId
          ? { isLoading: false, conversationId: null }
          : current
      );
      throw error;
    }
  };

  const sendMessage = async (
    text: string,
    searchOptions: SearchOption[],
    filter: SendMessageContextFilter[],
    workspaceDocuments?: ConversationWorkspaceDocumentSnapshot[],
    options?: SendMessageOptions
  ) => {
    if (!navigator.onLine) {
      setConnectionState("Disconnected");
      setLoading(true);
      return;
    }

    const isRetry = Boolean(options?.retryFailedAiMessageId);

    if (hasActiveNonTerminalStream(inFlightStreamsRef.current)) {
      showWarningToast(
        "Vent på at det aktive svar er færdigt, før du sender en ny besked"
      );
      return;
    }

    clearInFlightStreamingUi();
    if (!isRetry) {
      clearInFlightHumanMessageTracking();
    }
    setWaitingForResponse(true);
    setConnectionState("Connected");
    setLoading(false);

    const tempHumanMessage = { ...TEMP_HUMAN_MESSAGE, text };
    const tempAiMessage = {
      ...TEMP_AI_MESSAGE,
      conversationId: selectedConversation?.id ?? "",
    };

    if (isRetry && options?.retryFailedAiMessageId && selectedConversation) {
      const failedId = options.retryFailedAiMessageId;
      const conversationId = selectedConversation.id;
      const existingMessages = selectedConversation.messages ?? [];
      const failedIndex = existingMessages.findIndex(
        (message) => message.id === failedId
      );
      const humanMessage =
        failedIndex > 0 ? existingMessages[failedIndex - 1] : undefined;

      if (failedIndex < 0 || !humanMessage?.human) {
        setWaitingForResponse(false);
        return;
      }

      const messageIdsToRemove = existingMessages
        .slice(failedIndex)
        .map((message) => message.id);
      const deleteResults = await Promise.all(
        messageIdsToRemove.map((id) =>
          messageService.deleteMessage({ conversationId, id })
        )
      );

      if (!deleteResults.every((result) => result.success)) {
        logger.error("Failed to delete messages during chat retry", {
          operation: "sendMessageRetryDelete",
          conversationId,
          failedAiMessageId: failedId,
        });
        resetStreamState();
        streamControllerRef.current = null;
        clearInFlightHumanMessageTracking();
        setWaitingForResponse(false);
        await refreshConversationMessagesFromServer(conversationId);
        const retryHumanText = humanMessage.text;
        const retrySearchOptions = selectedConversation.searchOptions ?? [];
        const retryFilter = selectedConversation.workspaceFilter ?? [];
        showErrorToastWithAction(
          "Beskeden kunne ikke sendes igen",
          () =>
            void sendMessage(
              retryHumanText,
              retrySearchOptions,
              retryFilter,
              undefined,
              {
                retryFailedAiMessageId: failedId,
              }
            )
        );
        return;
      }

      const truncatedMessages = existingMessages.slice(0, failedIndex);
      inFlightHumanMessageIdRef.current = humanMessage.id;
      inFlightHumanMessagePersistedRef.current = true;

      const messagesAfterRetry = [...truncatedMessages, tempAiMessage];
      setSelectedConversation((prevSelected) =>
        prevSelected && prevSelected.id === conversationId
          ? { ...prevSelected, messages: messagesAfterRetry }
          : prevSelected
      );
      setConversations((prevConversations) =>
        prevConversations.map((conversation) =>
          conversation.id === conversationId
            ? { ...conversation, messages: messagesAfterRetry }
            : conversation
        )
      );
    } else if (!selectedConversation) {
      const tempConversation: Conversation = {
        ...emptyConversation,
        id: "temp-id",
        title: "Unavngivet chat",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        messages: [tempHumanMessage, tempAiMessage],
        searchOptions: searchOptions,
        workspaceFilter: filter,
      };

      setConversations((prevConversations) => [
        ...prevConversations,
        tempConversation,
      ]);

      setSelectedConversation(tempConversation);
    } else if (!isRetry) {
      setSelectedConversation((prevSelected) => {
        if (prevSelected) {
          return {
            ...prevSelected,
            messages: [
              ...(prevSelected.messages || []),
              tempHumanMessage,
              tempAiMessage,
            ],
          };
        }
        return prevSelected;
      });
    }

    let currentConversation = selectedConversation;

    if (!currentConversation || currentConversation.id === "temp-id") {
      const createdConversation = await conversationService.createConversation(
        searchOptions,
        filter,
        toneOfVoice?.id,
        workspaceDocuments
      );
      if (!createdConversation.success) {
        logger.error("Failed to create conversation", {
          operation: "createConversation",
          status: createdConversation.status,
        });
        setWaitingForResponse(false);
        return;
      }

      const createdPayload = createdConversation.value;
      const newConversationId = createdPayload.id;

      setSelectedConversation((prevSelected) => {
        if (prevSelected && prevSelected.id === "temp-id") {
          return {
            ...prevSelected,
            id: newConversationId,
            searchOptions: searchOptions,
            workspaceFilter: filter,
            toneOfVoiceId: createdPayload.toneOfVoiceId ?? toneOfVoice?.id,
            toneOfVoice: createdPayload.toneOfVoice,
            toneOfVoiceTitle: createdPayload.toneOfVoiceTitle,
            toneOfVoiceDescription: createdPayload.toneOfVoiceDescription,
          };
        }
        return prevSelected;
      });

      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          conv.id === "temp-id"
            ? {
                ...conv,
                id: newConversationId,
                toneOfVoiceId: createdPayload.toneOfVoiceId ?? toneOfVoice?.id,
                toneOfVoice: createdPayload.toneOfVoice,
                toneOfVoiceTitle: createdPayload.toneOfVoiceTitle,
                toneOfVoiceDescription: createdPayload.toneOfVoiceDescription,
                searchOptions,
                workspaceFilter: filter,
              }
            : conv
        )
      );

      currentConversation = {
        ...(currentConversation ?? {
          id: newConversationId,
          title: "Unavngivet chat",
          messages: [],
          ratings: { thumbsUpCount: 0, thumbsDownCount: 0, starRating: null },
          messageCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isWordConversation: false,
          searchOptions: [],
          workspaceFilter: [],
          toneOfVoiceId: undefined,
          toneOfVoice: "",
          toneOfVoiceTitle: "",
          toneOfVoiceDescription: "",
        }),
        id: newConversationId,
        searchOptions: searchOptions,
        workspaceFilter: filter,
        toneOfVoiceId: createdPayload.toneOfVoiceId ?? toneOfVoice?.id,
        toneOfVoice: createdPayload.toneOfVoice,
        toneOfVoiceTitle: createdPayload.toneOfVoiceTitle,
        toneOfVoiceDescription: createdPayload.toneOfVoiceDescription,
      };
    }

    let languageCode = "en";
    if (selectedLanguage) languageCode = selectedLanguage.code;

    const request = {
      text,
      conversationId: currentConversation.id,
      language: languageCode,
      chainOfThought: useChainOfThought,
    };

    const prepareStreamResponse = await messageService.prepareStream(request);

    if (!prepareStreamResponse.success) {
      logger.error("Failed to prepare the chat stream", {
        operation: "sendMessage",
        conversationId: request.conversationId,
        status: prepareStreamResponse.status,
      });
      failedSendMessageHandler(
        request,
        searchOptions,
        filter,
        workspaceDocuments
      );
      return;
    }

    if (!isRetry) {
      const humanMessageId = uuidv4();
      inFlightHumanMessageIdRef.current = humanMessageId;

      const humanPersistResult = await messageService.saveCompletedMessage({
        id: humanMessageId,
        conversationId: currentConversation.id,
        human: true,
        text,
        references: [],
        language: languageCode,
        isClosed: true,
      });

      if (!humanPersistResult.success) {
        logger.error("Failed to save the user's chat message", {
          operation: "sendMessage",
          conversationId: request.conversationId,
          status: humanPersistResult.status,
        });
        failedSendMessageHandler(
          request,
          searchOptions,
          filter,
          workspaceDocuments
        );
        return;
      }

      inFlightHumanMessagePersistedRef.current = true;

      // Record the PII-override audit (LAR-27). Fire-and-forget: it must never
      // block or fail the send. The audit is server-side always-on; a failure
      // here just means this one record is missed, so log at warn. Skipped
      // while the PII-Redaction flag is off (no scan happens, so overrides
      // can't exist — this guard just makes the kill-switch explicit).
      const piiOverride = options?.piiOverride;
      if (piiFeatureEnabled && piiOverride && piiOverride.count > 0) {
        void piiOverrideService
          .record({
            conversationId: currentConversation.id,
            messageId: humanMessageId,
            overriddenCount: piiOverride.count,
            overriddenTypes: piiOverride.types,
            hardIdOverridden: piiOverride.hardOverridden,
          })
          .then((res) => {
            if (!res.success) {
              logger.warn("Failed to record PII override audit", {
                operation: "recordPiiOverride",
                conversationId: currentConversation.id,
                status: res.status,
              });
            }
          });
      }
    }

    try {
      const token = await getCachedToken();
      const streamRequest = isRetry
        ? {
            ...prepareStreamResponse.value,
            memory: trimMemoryForRetry(
              prepareStreamResponse.value.memory,
              text
            ),
            generateTitle: false,
            sessionId,
          }
        : {
            ...prepareStreamResponse.value,
            sessionId,
          };

      streamContextRef.current = {
        conversationId: currentConversation.id,
        languageCode,
        humanText: text,
        searchOptions,
        filter,
        workspaceDocuments,
        documentWorkspaceMap: prepareStreamResponse.value.documentWorkspaceMap,
        isRetry,
      };

      lastChunkSeqRef.current = 0;

      const generationId = await startChatGeneration(streamRequest, token);

      generationIdRef.current = generationId;
      streamTenantIdRef.current = prepareStreamResponse.value.tenantId;
      activeStreamConversationIdRef.current = currentConversation.id;

      if (recoveryUserId) {
        upsertPendingLedger({
          generationId,
          conversationId: currentConversation.id,
          userId: recoveryUserId,
          tenantId: prepareStreamResponse.value.tenantId,
          lastChunkSeq: 0,
          phase: "streaming",
          streamContext: streamContextRef.current,
          inFlightHumanMessageId: inFlightHumanMessageIdRef.current,
          inFlightHumanMessagePersisted:
            inFlightHumanMessagePersistedRef.current,
        });
      }
      const initialStreamState = {
        ...createInFlightStreamState(currentConversation.id, generationId),
        lastChunkSeq: lastChunkSeqRef.current,
        streamContext: streamContextRef.current,
      };
      inFlightStreamsRef.current.set(
        currentConversation.id,
        initialStreamState
      );
      syncActiveStreamToMap(currentConversation.id, { persist: "immediate" });

      streamControllerRef.current = subscribeChatGeneration({
        generationId,
        afterSeq: 0,
        callbacks: buildStreamCallbacks(),
        token,
        onSeq: (seq) => {
          lastChunkSeqRef.current = seq;
          syncActiveStreamToMap(currentConversation.id, {
            persist: "throttled",
          });
        },
      });
    } catch (error) {
      logger.error("Failed to start the chat stream", {
        operation: "sendMessage",
        error,
        conversationId: request.conversationId,
      });
      failedSendMessageHandler(
        request,
        searchOptions,
        filter,
        workspaceDocuments
      );
    }
  };

  const executeRetryFailedResponse = async (failedAiMessage: Message) => {
    if (!selectedConversation || waitingForResponse) {
      return;
    }

    const messages = selectedConversation.messages ?? [];
    const failedIndex = messages.findIndex(
      (message) => message.id === failedAiMessage.id
    );
    const humanMessage =
      failedIndex > 0 ? messages[failedIndex - 1] : undefined;

    if (!humanMessage?.human) {
      return;
    }

    await sendMessage(
      humanMessage.text,
      selectedConversation.searchOptions ?? [],
      selectedConversation.workspaceFilter ?? [],
      undefined,
      { retryFailedAiMessageId: failedAiMessage.id }
    );
  };

  const retryFailedResponse = async (failedAiMessage: Message) => {
    if (!selectedConversation || waitingForResponse) {
      return;
    }

    const messages = selectedConversation.messages ?? [];
    if (willRetryResetConversation(messages, failedAiMessage.id)) {
      setRetryResetConfirmation(failedAiMessage);
      return;
    }

    await executeRetryFailedResponse(failedAiMessage);
  };

  const failedSendMessageHandler = async (
    request: { text: string; conversationId: string },
    searchOptions: SearchOption[] = [],
    filter: SendMessageContextFilter[] = [],
    workspaceDocuments?: ConversationWorkspaceDocumentSnapshot[]
  ) => {
    clearInFlightStreamingUi();
    clearInFlightHumanMessageTracking();
    setWaitingForResponse(false);

    showErrorToastWithAction("Beskeden kunne ikke sendes", () =>
      sendMessage(request.text, searchOptions, filter, workspaceDocuments)
    );
  };

  // Modify the existing fetchConversations logic
  const fetchConversations = async (page: number) => {
    setLoading(true);
    const fetchedConversations = await conversationService.queryPagination({
      pageNumber: page,
      pageSize: pageSize,
      sortAscending: false,
      search: "",
      sortBy: "createdAt",
      filters: [
        {
          value: "false",
          id: "isWordConversation",
        },
      ],
    });

    if (!fetchedConversations.success) {
      throw restError(fetchedConversations, "Failed to fetch conversations");
    }

    setPageCount(fetchedConversations.value.totalPages);

    // Update conversations
    setConversations((prevConversations) => {
      const tempConversation = prevConversations.find(
        (conv) => conv.id === "temp-id"
      );
      const otherConversations = fetchedConversations.value.data.filter(
        (conv) => conv.id !== "temp-id"
      );

      const merged = tempConversation
        ? [tempConversation, ...otherConversations]
        : otherConversations;

      return merged.filter((conv) => !conv.isWordConversation);
    });
    setLoading(false);
  };

  // Add pagination handlers
  const handleNextPage = async () => {
    if (pageNumber < pageCount) {
      setPageNumber((prev) => prev + 1);
      await fetchConversations(pageNumber + 1);
    }
  };

  const handlePreviousPage = async () => {
    if (pageNumber > 1) {
      setPageNumber((prev) => prev - 1);
      await fetchConversations(pageNumber - 1);
    }
  };

  const handlePageChange = async (page: number) => {
    setPageNumber(page);
    await fetchConversations(page);
  };

  /* __________________Context Value__________________ */
  // Memoize the context value to avoid unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      conversations,
      selectedConversation,
      latestMessage,
      waitingForResponse,
      loading,
      isLoadingMessages,
      selectedLanguage,
      connectionState,
      setSelectedLanguage,
      selectConversation,
      selectConversationById,
      deleteConversation,
      rateMessage,
      sendMessage,
      retryFailedResponse,
      manualReconnect,
      useChainOfThought,
      setUseChainOfThought,
      toneOfVoice,
      setToneOfVoice,
      // Add pagination values
      pageNumber,
      pageCount,
      handleNextPage,
      handlePreviousPage,
      setPageNumber: handlePageChange,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      conversations,
      selectedConversation,
      latestMessage,
      isLoadingMessages,
      waitingForResponse,
      loading,
      selectedLanguage,
      useChainOfThought,
      pageNumber,
      pageCount,
      toneOfVoice,
      // Unleash resolves async (off → on after boot); without this, consumers
      // keep a sendMessage closure that still sees the flag as off and skips
      // the PII-override audit POST.
      piiFeatureEnabled,
    ]
  );

  // Prevent timeout leaks on unmount
  useEffect(
    () => () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      streamControllerRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    const handlePageHide = () => {
      flushPersistedInFlightStreams();
    };

    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handlePageHide);
    };
    // Handler reads recoveryUserIdRef so login hydration is visible on pagehide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ConversationContext value={contextValue}>
      {children}
      {retryResetConfirmation && (
        <ConfirmationDialog
          isOpen
          onClose={() => setRetryResetConfirmation(null)}
          title="Genskab samtalen fra dette tidspunkt?"
          description="Hvis du prøver igen på denne besked, fjernes den fejlede besked og alle senere beskeder i samtalen. Samtalen fortsætter som om du lige havde sendt dette spørgsmål."
          attentionText="Senere beskeder i samtalen slettes permanent."
          confirmText="Prøv igen"
          confirmIcon={<RefreshCw className="size-4" aria-hidden />}
          variant="destructive"
          onConfirm={() => {
            const failedAiMessage = retryResetConfirmation;
            setRetryResetConfirmation(null);
            void executeRetryFailedResponse(failedAiMessage);
          }}
        />
      )}
    </ConversationContext>
  );
};

export default ConversationHubProvider;
